import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Building2, 
  Calendar, 
  MapPin, 
  Target, 
  ArrowLeft, 
  Briefcase, 
  Users, 
  Clock, 
  DollarSign,
  CheckCircle2,
  Package,
  UserRound,
  Info,
  Percent,
  X,
  Pencil,
  Check,
  Search
} from "lucide-react";
import { format } from "date-fns";
import { fetchRfqById, fetchOpportunityById, submitRfqResponse } from "@/lib/api/campaign";
import { Opportunity } from "@/hooks/useOpportunitiesApi";
import { useToast } from "@/components/ui/use-toast";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import * as z from "zod";

interface RFQ {
  id: string;
  campaign_id: string;
  meta: {
    additional_notes?: string;
  };
  status: number;
  created_at: string;
  updated_at: string;
  event_campaign: {
    name: string;
    meta: {
      goals?: string[];
      theme?: string;
      locations?: {
        state: string;
        country: string;
        district: string;
        meta: {
          details?: string;
          volunteers: number;
        };
      }[];
      description?: string;
      opportunities?: string[];
      additional_details?: string;
    };
    active_from: string;
    active_to: string;
  };
  corporate: {
    id: string;
    org_name: string;
    poc_first_name?: string;
    profile_photo_url?: string | null;
  };
}

// Schema for the response form
const responseFormSchema = z.object({
  // Step 1: Opportunities
  supportedOpportunities: z.array(z.string()).min(1, "Please select at least one opportunity"),
  
  // Step 2: Opportunity-specific locations and pricing
  opportunityRateCards: z.record(z.object({
    // Locations for this opportunity
    supportedLocations: z.array(z.string()).min(1, "Please select at least one location for this opportunity"),
    
    basePricingVariant: z.object({
      pricingModel: z.enum(["per_volunteer", "per_event"]),
      costPerUnit: z.string().refine(val => !isNaN(Number(val)) && Number(val) > 0, {
        message: "Please enter a valid cost greater than 0",
      }),
      minVolunteers: z.string().refine(val => !isNaN(Number(val)) && Number(val) > 0, {
        message: "Please enter a valid number greater than 0",
      }).optional(),
    }),
    maxVolunteers: z.string().refine(val => !isNaN(Number(val)) && Number(val) > 0, {
      message: "Please enter a valid number greater than 0",
    }),
    
    // Additional cost factors
    programManagementFee: z.string().optional(),
    programManagementFeeType: z.enum(["total", "per_event", "per_volunteer"]).default("total"),
    materialKitFee: z.string().optional(),
    materialKitFeeType: z.enum(["total", "per_event", "per_volunteer"]).default("per_volunteer"),
    refreshmentsFee: z.string().optional(),
    refreshmentsFeeType: z.enum(["total", "per_event", "per_volunteer"]).default("per_event"),
    goodiesFee: z.string().optional(),
    goodiesFeeType: z.enum(["total", "per_event", "per_volunteer"]).default("per_volunteer"),
    
    // Geographic pricing adjustments
    enableGeographicPricing: z.boolean().default(false),
    geographicMarkups: z.array(
      z.object({
        locationId: z.string(),
        markupPercentage: z.string().refine(val => !isNaN(Number(val)) && Number(val) >= 0, {
          message: "Please enter a valid percentage (0 or greater)",
        }),
      })
    ).default([]),
    
    // Include these fees in base price or itemize separately
    includeFees: z.boolean().default(true),
    
    // Track if this rate card was initialized with recommendations
    _initialized_with_recommendations: z.boolean().optional(),
  })),
  
  currency: z.string().min(1, "Please select a currency"),
  
  additionalDetails: z.string().optional(),
});

type ResponseFormValues = z.infer<typeof responseFormSchema>;

// Currency options
const currencies = [
  { label: "USD - US Dollar", value: "USD" },
  { label: "EUR - Euro", value: "EUR" },
  { label: "GBP - British Pound", value: "GBP" },
  { label: "JPY - Japanese Yen", value: "JPY" },
  { label: "CAD - Canadian Dollar", value: "CAD" },
  { label: "AUD - Australian Dollar", value: "AUD" },
  { label: "SGD - Singapore Dollar", value: "SGD" },
  { label: "INR - Indian Rupee", value: "INR" },
];

// Exchange rates to USD (simplified for demo)
const exchangeRates: Record<string, number> = {
  USD: 1,
  EUR: 1.09,
  GBP: 1.27,
  JPY: 0.0067,
  CAD: 0.74,
  AUD: 0.66,
  SGD: 0.75,
  INR: 0.012,
};

export default function NpoRfqResponsePage({ session, profile }: { session: any; profile: any }) {
  const { id: rfqId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [opportunitiesData, setOpportunitiesData] = useState<Record<string, Opportunity>>({});
  const [loadingOpportunities, setLoadingOpportunities] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRecommendations, setPriceRecommendations] = useState<Record<string, any>>({});
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  
  // Helper function to get fee type label
  const getFeeTypeLabel = (feeType: string) => {
    switch (feeType) {
      case "total": return "total program cost";
      case "per_event": return "per event";
      case "per_volunteer": return "per volunteer";
      default: return "";
    }
  };
  const [globalSupportedLocations, setGlobalSupportedLocations] = useState<string[]>([]);
  
  // Function to fetch price recommendations for selected opportunities
  const fetchPriceRecommendations = async (opportunityIds: string[]) => {
    if (!session?.access_token || opportunityIds.length === 0) {
      setLoadingRecommendations(false);
      return;
    }
    
    setLoadingRecommendations(true);
    try {
      // Construct URL with ids[] array format in query parameters
      const url = new URL(`${import.meta.env.VITE_CC_API_URL}/v1/opportunities/price-recommendations`);
      
      // Add each ID as a separate ids[] parameter
      opportunityIds.forEach(id => {
        url.searchParams.append('ids[]', id);
      });
      
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (response.ok) {
        const responseData = await response.json();
        console.log("Price recommendation API response:", responseData);
        
        // Process the response data which is in an array format
        if (responseData.data && Array.isArray(responseData.data)) {
          const processedRecommendations: Record<string, any> = {};
          let globalSupportedLocations: string[] = [];
          let globalCurrency: string | undefined;
          let supportedOpportunitiesFromRecommendations: string[] = [];
          
          // Process each item in the data array
          responseData.data.forEach(item => {
            // Store supported locations from the recommendation
            if (item.meta?.supported_locations && Array.isArray(item.meta.supported_locations)) {
              globalSupportedLocations = [...item.meta.supported_locations];
            }
            
            // Store supported opportunities from recommendation for preselection
            if (item.meta?.supported_opportunities && Array.isArray(item.meta.supported_opportunities)) {
              supportedOpportunitiesFromRecommendations = [...item.meta.supported_opportunities];
            }
            
            // Check if meta and opportunity_responses exist
            if (item.meta?.opportunity_responses) {
              // Loop through all opportunity responses in this item
              Object.entries(item.meta.opportunity_responses).forEach(([opportunityId, responseData]: [string, any]) => {
                // Only process if this opportunity is one we're interested in
                // and it has been fetched already
                if (opportunityIds.includes(opportunityId) && fetchedOpportunitiesRef.current.has(opportunityId)) {
                  // Store the currency for global settings
                  if (responseData.currency && !globalCurrency) {
                    globalCurrency = responseData.currency;
                  }
                  
                  // Check if all additional fees are zero to determine if we should check the box by default
                  const allFeesAreZero = 
                    (responseData.additional_fees?.program_management?.amount || 0) === 0 &&
                    (responseData.additional_fees?.material_kit?.amount || 0) === 0 &&
                    (responseData.additional_fees?.refreshments?.amount || 0) === 0 &&
                    (responseData.additional_fees?.goodies?.amount || 0) === 0;
                  
                  // Map the API response to our expected format
                  processedRecommendations[opportunityId] = {
                    pricing_model: responseData.pricing_model,
                    base_cost: parseFloat(responseData.cost_per_unit),
                    min_volunteers: responseData.min_volunteers,
                    max_volunteers: responseData.max_volunteers_per_event,
                    program_management_fee: responseData.additional_fees?.program_management?.amount || 0,
                    program_management_fee_type: responseData.additional_fees?.program_management?.fee_type || "total",
                    material_kit_fee: responseData.additional_fees?.material_kit?.amount || 0,
                    material_kit_fee_type: responseData.additional_fees?.material_kit?.fee_type || "per_volunteer",
                    refreshments_fee: responseData.additional_fees?.refreshments?.amount || 0,
                    refreshments_fee_type: responseData.additional_fees?.refreshments?.fee_type || "per_event",
                    goodies_fee: responseData.additional_fees?.goodies?.amount || 0,
                    goodies_fee_type: responseData.additional_fees?.goodies?.fee_type || "per_volunteer",
                    include_fees_in_base_price: responseData.include_fees_in_base !== undefined ? responseData.include_fees_in_base : allFeesAreZero,
                    currency: responseData.currency,
                    supported_locations: globalSupportedLocations,
                    event_type: responseData.event_type,
                    additional_details: responseData.additional_details
                  };
                }
              });
            }
          });
          
          // Set the recommendations state
          setPriceRecommendations(prev => ({ ...prev, ...processedRecommendations }));
          
          // Pre-select all opportunities that overlap with recommendations
          const availableOpportunityIds = rfq?.event_campaign?.meta?.opportunities || [];
          
          // Find opportunities that overlap between available opportunities and recommendations
          const overlappingOpportunities = availableOpportunityIds.filter(oppId => 
            // Include if it's in the supported opportunities from recommendations
            supportedOpportunitiesFromRecommendations.includes(oppId) ||
            // Or if we have pricing recommendations for it
            Object.keys(processedRecommendations).includes(oppId)
          );
          
          // Preselect the overlapping opportunities
          if (overlappingOpportunities.length > 0) {
            form.setValue("supportedOpportunities", overlappingOpportunities);
          }
          
          if (globalSupportedLocations.length > 0) {
            // This will be used in Step 1 to show the supported locations
            setGlobalSupportedLocations(globalSupportedLocations);
          }
          
          // Set the global currency if available
          if (globalCurrency) {
            form.setValue("currency", globalCurrency);
          }
          
          // Force re-initialization of rate cards for these opportunities
          const currentRateCards = form.getValues("opportunityRateCards");
          opportunityIds.forEach(id => {
            if (processedRecommendations[id] && currentRateCards[id]) {
              delete currentRateCards[id];
            }
          });
          form.setValue("opportunityRateCards", currentRateCards);
        }
      } else {
        console.error("Failed to fetch price recommendations: Server returned", response.status);
      }
    } catch (err) {
      console.error("Failed to fetch price recommendations:", err);
    } finally {
      setLoadingRecommendations(false);
    }
  };
  
  // Form setup
  const form = useForm<ResponseFormValues>({
    resolver: zodResolver(responseFormSchema),
    defaultValues: {
      supportedOpportunities: [],
      opportunityRateCards: {},
      currency: "USD",
      additionalDetails: "",
    },
  });

  // Fetch RFQ data
  const { 
    data: rfqData, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['rfq', rfqId],
    queryFn: () => fetchRfqById(session?.access_token, rfqId as string),
    enabled: !!session?.access_token && !!rfqId,
  });

  const rfq: RFQ | undefined = rfqData?.data;

  // Function to fetch opportunity details
  const fetchOpportunityDetails = async (opportunityId: string) => {
    if (!session?.access_token) return;
    
    // Set loading state for this opportunity
    setLoadingOpportunities(prev => ({ ...prev, [opportunityId]: true }));
    
    try {
      const result = await fetchOpportunityById(session.access_token, opportunityId);
      
      if (result && result.data) {
        // Store the opportunity data
        setOpportunitiesData(prev => ({
          ...prev,
          [opportunityId]: result.data
        }));
      }
    } catch (error) {
      console.error("Error fetching opportunity:", error);
    } finally {
      // Clear loading state
      setLoadingOpportunities(prev => ({ ...prev, [opportunityId]: false }));
    }
  };

  // Load opportunity details when RFQ data is available
  const fetchedOpportunitiesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (rfq?.event_campaign?.meta?.opportunities && rfq.event_campaign.meta.opportunities.length > 0) {
      // First, fetch all opportunity details
      rfq.event_campaign.meta.opportunities.forEach(opportunityId => {
        if (!fetchedOpportunitiesRef.current.has(opportunityId)) {
          fetchedOpportunitiesRef.current.add(opportunityId);
          fetchOpportunityDetails(opportunityId);
        }
      });
      
      // Fetch price recommendations for all opportunities in step 1
      // This ensures we have the recommendations before user selects opportunities
      const fetchedOpportunities = rfq.event_campaign.meta.opportunities.filter(
        opportunityId => fetchedOpportunitiesRef.current.has(opportunityId)
      );
      
      if (fetchedOpportunities.length > 0) {
        fetchPriceRecommendations(fetchedOpportunities);
      }
    }
  }, [rfq?.event_campaign?.meta?.opportunities, session?.access_token]);

  // Initialize rate cards for selected opportunities
  useEffect(() => {
    const selectedOpportunities = form.watch("supportedOpportunities");
    const currentRateCards = form.watch("opportunityRateCards");
    
    // For each selected opportunity, create a rate card if it doesn't exist
    selectedOpportunities.forEach(opportunityId => {
      // Always re-initialize if we have recommendations
      const shouldInitialize = !currentRateCards[opportunityId] || 
                              (priceRecommendations[opportunityId] && 
                               !currentRateCards[opportunityId]?._initialized_with_recommendations);
      
      if (shouldInitialize) {
        // Get the opportunity format
        const opportunity = opportunitiesData[opportunityId];
        let eventType = "Virtual"; // Default
        
        // Map the opportunity format to event type
        if (opportunity?.format) {
          if (opportunity.format === "In_Office") {
            eventType = "InOffice";
          } else if (opportunity.format === "Outdoor") {
            eventType = "Outdoor";
          } else if (opportunity.format === "Virtual") {
            eventType = "Virtual";
          }
        }
        
        // Check if we have price recommendations for this opportunity
        const recommendation = priceRecommendations[opportunityId];
        
        // If we have a recommendation with a currency, set it as the form currency
        if (recommendation?.currency) {
          form.setValue("currency", recommendation.currency);
        }
        
        console.log("Initializing rate card for", opportunityId, "with recommendation:", recommendation);
        
        // Get the recommended event type or use the one from opportunity format
        const recommendedEventType = recommendation?.event_type || eventType;
        
        // Determine if the pricing model should be restricted based on event type
        // For Virtual events, we typically use per_event, for others we use per_volunteer by default
        const defaultPricingModel = recommendedEventType === "Virtual" ? "per_event" : "per_volunteer";
        
        // Check if all additional fees are zero
        const allFeesAreZero = 
          (recommendation?.program_management_fee || 0) === 0 &&
          (recommendation?.material_kit_fee || 0) === 0 &&
          (recommendation?.refreshments_fee || 0) === 0 &&
          (recommendation?.goodies_fee || 0) === 0;
        
        // Initialize the rate card with default or recommended values
        form.setValue(`opportunityRateCards.${opportunityId}`, {
          // Use supported locations from recommendations if available
          supportedLocations: globalSupportedLocations.length > 0 ? globalSupportedLocations : (recommendation?.supported_locations || []),
          basePricingVariant: {
            pricingModel: recommendation?.pricing_model || defaultPricingModel,
            costPerUnit: recommendation?.base_cost?.toString() || "",
            minVolunteers: recommendation?.min_volunteers?.toString() || "",
          },
          maxVolunteers: recommendation?.max_volunteers?.toString() || "",
          programManagementFee: recommendation?.program_management_fee?.toString() || "",
          programManagementFeeType: recommendation?.program_management_fee_type || "total",
          materialKitFee: recommendation?.material_kit_fee?.toString() || "",
          materialKitFeeType: recommendation?.material_kit_fee_type || "per_volunteer",
          refreshmentsFee: recommendation?.refreshments_fee?.toString() || "",
          refreshmentsFeeType: recommendation?.refreshments_fee_type || "per_event",
          goodiesFee: recommendation?.goodies_fee?.toString() || "",
          goodiesFeeType: recommendation?.goodies_fee_type || "per_volunteer",
          // Skip geographic pricing adjustments as requested
          enableGeographicPricing: false,
          geographicMarkups: [],
          // If all fees are 0, check the box by default
          includeFees: recommendation?.include_fees_in_base_price !== undefined ? 
                       recommendation.include_fees_in_base_price : 
                       allFeesAreZero,
          // Add a flag to indicate this was initialized with recommendations
          _initialized_with_recommendations: !!recommendation,
          // Store the event type for reference
          eventType: recommendedEventType
        });
        
        // If we have a recommendation with additional details, set it in the form
        if (recommendation?.additional_details) {
          form.setValue("additionalDetails", recommendation.additional_details);
        }
      }
    });
    
    // Remove rate cards for deselected opportunities
    Object.keys(currentRateCards).forEach(opportunityId => {
      if (!selectedOpportunities.includes(opportunityId)) {
        const newRateCards = { ...currentRateCards };
        delete newRateCards[opportunityId];
        form.setValue("opportunityRateCards", newRateCards);
      }
    });
  }, [form.watch("supportedOpportunities"), opportunitiesData, priceRecommendations]);

  // Submit RFQ response
  const submitMutation = useMutation({
    mutationFn: (data: ResponseFormValues) => {
      if (!rfqId || !session?.access_token) {
        throw new Error("Missing RFQ ID or session token");
      }

      // Prepare the payload
      const payload = {
        rfq_id: rfqId,
        meta: {
          supported_locations: [], // Will collect all unique locations from opportunity rate cards
          supported_opportunities: data.supportedOpportunities || [],
          opportunity_responses: {}
        }
      };
      
      // Collect all unique supported locations across all opportunities
      const allLocations = new Set<string>();
      data.supportedOpportunities.forEach(opportunityId => {
        const rateCard = data.opportunityRateCards[opportunityId];
        if (rateCard && rateCard.supportedLocations) {
          rateCard.supportedLocations.forEach(location => allLocations.add(location));
        }
      });
      
      // Add all unique locations to the payload
      payload.meta.supported_locations = Array.from(allLocations);

      // Process each opportunity rate card
      data.supportedOpportunities.forEach(opportunityId => {
        const rateCard = data.opportunityRateCards[opportunityId];
        if (!rateCard) return;

        // Get opportunity format to determine event type
        const opportunity = opportunitiesData[opportunityId];
        let eventType = "Virtual"; // Default
        if (opportunity?.format) {
          if (opportunity.format === "In_Office") {
            eventType = "InOffice";
          } else if (opportunity.format === "Outdoor") {
            eventType = "Outdoor";
          } else if (opportunity.format === "Virtual") {
            eventType = "Virtual";
          }
        }

        // Preserve original values and calculate USD equivalents separately
        const originalBaseCost = Number(rateCard.basePricingVariant.costPerUnit);
        const baseCostUSD = originalBaseCost * exchangeRates[data.currency];
        
        const originalProgramFee = rateCard.programManagementFee ? parseFloat(rateCard.programManagementFee) : 0;
        const programFeeUSD = originalProgramFee * exchangeRates[data.currency];
        
        const originalMaterialFee = rateCard.materialKitFee ? parseFloat(rateCard.materialKitFee) : 0;
        const materialFeeUSD = originalMaterialFee * exchangeRates[data.currency];
        
        const originalRefreshmentsFee = rateCard.refreshmentsFee ? parseFloat(rateCard.refreshmentsFee) : 0;
        const refreshmentsFeeUSD = originalRefreshmentsFee * exchangeRates[data.currency];
        
        const originalGoodiesFee = rateCard.goodiesFee ? parseFloat(rateCard.goodiesFee) : 0;
        const goodiesFeeUSD = originalGoodiesFee * exchangeRates[data.currency];

        // Create opportunity response object with original values and USD equivalents
        payload.meta.opportunity_responses[opportunityId] = {
          event_type: eventType,
          pricing_model: rateCard.basePricingVariant.pricingModel,
          cost_per_unit: originalBaseCost, // Use original value entered by user
          cost_per_unit_usd: baseCostUSD, // Include USD equivalent for backend calculations
          min_volunteers: rateCard.basePricingVariant.minVolunteers,
          include_fees_in_base: rateCard.includeFees,
          additional_fees: {
            program_management: {
              amount: originalProgramFee, // Use original value entered by user
              amount_usd: programFeeUSD, // Include USD equivalent
              fee_type: rateCard.programManagementFeeType
            },
            material_kit: {
              amount: originalMaterialFee, // Use original value entered by user
              amount_usd: materialFeeUSD, // Include USD equivalent
              fee_type: rateCard.materialKitFeeType
            },
            refreshments: {
              amount: originalRefreshmentsFee, // Use original value entered by user
              amount_usd: refreshmentsFeeUSD, // Include USD equivalent
              fee_type: rateCard.refreshmentsFeeType
            },
            goodies: {
              amount: originalGoodiesFee, // Use original value entered by user
              amount_usd: goodiesFeeUSD, // Include USD equivalent
              fee_type: rateCard.goodiesFeeType
            }
          },
          currency: data.currency,
          max_volunteers_per_event: rateCard.maxVolunteers,
          additional_details: data.additionalDetails || ""
        };

        // Add geographic pricing if enabled
        if (rateCard.enableGeographicPricing && rateCard.geographicMarkups && rateCard.geographicMarkups.length > 0) {
          payload.meta.opportunity_responses[opportunityId].geographic_pricing = {
            enabled: true,
            markups: rateCard.geographicMarkups.map(markup => {
              // Format the location name for display
              const locationParts = markup.locationId.split('-');
              const locationName = locationParts.length === 3 
                ? `${locationParts[2]}, ${locationParts[1]}, ${locationParts[0]}`
                : markup.locationId;
              
              return {
                location_id: markup.locationId,
                location_name: locationName,
                markup_percentage: parseFloat(markup.markupPercentage)
              };
            })
          };
        }
      });

      // Submit the payload
      return fetch(`${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq/${rfqId}/rate-card`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify(payload)
      }).then(response => {
        if (!response.ok) {
          throw new Error(`Error submitting RFQ response: ${response.status}`);
        }
        return response.json();
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Your RFQ response has been submitted successfully.",
        variant: "default",
      });
      // Redirect to the RFQs dashboard
      navigate("/dashboard/corporate-events/rfqs");
    },
    onError: (error) => {
      console.error("Error submitting RFQ response:", error);
      toast({
        title: "Error",
        description: `Failed to submit RFQ response: ${error.message}`,
        variant: "destructive",
      });
    }
  });

  const onSubmit = (data: ResponseFormValues) => {
    submitMutation.mutate(data);
  };

  const nextStep = async () => {
    if (currentStep === 1) {
      // Validate opportunities
      const opportunities = form.getValues("supportedOpportunities");
      if (opportunities.length === 0) {
        form.setError("supportedOpportunities", {
          type: "manual",
          message: "Please select at least one opportunity",
        });
        return;
      }
      
      // If recommendations are still loading, don't proceed
      if (loadingRecommendations) {
        return;
      }
      
      // We've already fetched price recommendations in step 1, so we can proceed directly
      // If there are any opportunities without recommendations, we don't need to fetch them again
      // as we'll use the same recommended data set from step 1
    } else if (currentStep === 2) {
      // Validate that each selected opportunity has at least one location
      const opportunities = form.getValues("supportedOpportunities");
      let hasError = false;
      
      opportunities.forEach(opportunityId => {
        const locations = form.getValues(`opportunityRateCards.${opportunityId}.supportedLocations`);
        if (!locations || locations.length === 0) {
          form.setError(`opportunityRateCards.${opportunityId}.supportedLocations`, {
            type: "manual",
            message: "Please select at least one location for this opportunity",
          });
          hasError = true;
        }
      });
      
      if (hasError) return;
    }
    
    setCurrentStep(prev => Math.min(prev + 1, 2));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/corporate-events/rfqs')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to RFQs
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-full max-w-md" />
            <Skeleton className="h-4 w-full max-w-sm mt-2" />
          </CardHeader>
          <CardContent className="space-y-6">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error || !rfq) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/corporate-events/rfqs')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to RFQs
          </Button>
        </div>
        
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Error Loading RFQ</CardTitle>
            <CardDescription>
              We couldn't load the RFQ details. Please try again or contact support.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : "Unknown error occurred"}
            </p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/dashboard/corporate-events/rfqs')}>
              Return to RFQs
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/corporate-events/rfqs')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to RFQs
        </Button>
        <h1 className="text-2xl font-bold">Respond to RFQ</h1>
      </div>
      
      <div className="space-y-4">
        {/* Compact RFQ details banner */}
        <div className="w-full">
          <div className="bg-muted/40 p-4 rounded-lg border">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                <div>
                  <h3 className="font-medium">{rfq?.event_campaign?.name}</h3>
                  <p className="text-sm text-muted-foreground">Request from {rfq?.corporate?.org_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {rfq?.event_campaign?.active_from && rfq?.event_campaign?.active_to
                    ? `${format(new Date(rfq?.event_campaign?.active_from), 'MMM dd, yyyy')} - ${format(new Date(rfq?.event_campaign?.active_to), 'MMM dd, yyyy')}`
                    : "No date range specified"}
                </p>
              </div>
            </div>
            {rfq?.meta?.additional_notes && (
              <div className="flex items-start gap-2 mt-2 text-sm">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-muted-foreground">{rfq?.meta?.additional_notes}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Main content with response form */}
        <div className="w-full">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>RFQ Response</CardTitle>
                  <CardDescription>
                    Step {currentStep} of 2
                  </CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`h-2.5 w-2.5 rounded-full ${currentStep >= 1 ? 'bg-primary' : 'bg-muted'}`}></div>
                  <div className={`h-2.5 w-2.5 rounded-full ${currentStep >= 2 ? 'bg-primary' : 'bg-muted'}`}></div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Step 1: Opportunities */}
                  {currentStep === 1 && (
                    <div className="space-y-6">
                      {/* Notification about preselected opportunities */}
                      {form.watch("supportedOpportunities").length > 0 && (
                        <div className="bg-muted/50 border border-primary/20 rounded-md p-3 flex items-start gap-2">
                          <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">
                              {form.watch("supportedOpportunities").length} {form.watch("supportedOpportunities").length === 1 ? 'opportunity has' : 'opportunities have'} been preselected based on previous data.
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              You can modify these selections as needed before proceeding to the next step.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-5 w-5 text-primary" />
                          <h2 className="text-lg font-semibold">Supported Opportunities</h2>
                        </div>
                        <div className="relative w-full max-w-sm">
                          <Input
                            type="text"
                            placeholder="Search opportunities..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8"
                          />
                          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      
                      <FormField
                        control={form.control}
                        name="supportedOpportunities"
                        render={() => (
                          <FormItem>
                            <div className="mb-4">
                              <FormLabel>Select the opportunities you can support</FormLabel>
                              <FormDescription>
                                Choose the opportunities that your organization can provide for this campaign.
                              </FormDescription>
                            </div>
                            <ScrollArea className="h-[400px] pr-4">
                              <div className="space-y-4">
                                {rfq.event_campaign.meta.opportunities && rfq.event_campaign.meta.opportunities.length > 0 ? (
                                  rfq.event_campaign.meta.opportunities
                                    .filter(opportunityId => {
                                      if (!searchQuery) return true;
                                      const opportunity = opportunitiesData[opportunityId];
                                      if (!opportunity) return true; // Include loading items
                                      
                                      const searchLower = searchQuery.toLowerCase();
                                      return (
                                        (opportunity.name && opportunity.name.toLowerCase().includes(searchLower)) ||
                                        (opportunity.format && opportunity.format.toLowerCase().includes(searchLower)) ||
                                        (opportunity.focus_area && opportunity.focus_area.toLowerCase().includes(searchLower)) ||
                                        (opportunity.community_need && opportunity.community_need.toLowerCase().includes(searchLower)) ||
                                        (opportunity.volunteer_support && opportunity.volunteer_support.toLowerCase().includes(searchLower))
                                      );
                                    })
                                    .map((opportunityId) => (
                                      <FormField
                                        key={opportunityId}
                                        control={form.control}
                                        name="supportedOpportunities"
                                        render={({ field }) => {
                                          // Extract the checked state for this specific opportunity
                                          const isChecked = field.value?.includes(opportunityId) || false;
                                          
                                          return (
                                            <FormItem className="mb-3">
                                              <div 
                                                className={`relative rounded-md border p-4 overflow-hidden cursor-pointer transition-all ${isChecked ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/20'}`}
                                              >
                                                {/* Background image or gradient */}
                                                {opportunitiesData[opportunityId]?.image_url ? (
                                                  <div 
                                                    className="absolute inset-0 bg-cover bg-center opacity-10 z-0" 
                                                    style={{ 
                                                      backgroundImage: `url(${opportunitiesData[opportunityId].image_url})`,
                                                      backgroundSize: 'cover',
                                                      backgroundPosition: 'center'
                                                    }}
                                                  />
                                                ) : (
                                                  <div className="absolute inset-0 bg-gradient-to-r from-muted/30 to-background/10 opacity-50 z-0" />
                                                )}
                                                
                                                <div className="flex items-start gap-3 relative z-10">
                                                  {isChecked && (
                                                    <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-0.5">
                                                      <Check className="h-3 w-3" />
                                                    </div>
                                                  )}
                                                  {/* Checkbox */}
                                                  <FormControl>
                                                    <Checkbox
                                                      checked={isChecked}
                                                      onCheckedChange={(checked) => {
                                                        if (checked) {
                                                          field.onChange([...field.value || [], opportunityId]);
                                                        } else {
                                                          field.onChange(field.value.filter(id => id !== opportunityId));
                                                        }
                                                      }}
                                                    />
                                                  </FormControl>
                                                  
                                                  {/* Content */}
                                                  <div className="space-y-2 flex-1">
                                                    {loadingOpportunities[opportunityId] ? (
                                                      <div className="space-y-2">
                                                        <Skeleton className="h-5 w-48" />
                                                        <Skeleton className="h-4 w-full" />
                                                        <Skeleton className="h-4 w-3/4" />
                                                      </div>
                                                    ) : opportunitiesData[opportunityId] ? (
                                                      <div className="space-y-2">
                                                        <FormLabel className="font-medium text-lg cursor-pointer">
                                                          {opportunitiesData[opportunityId].name}
                                                        </FormLabel>
                                                        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                                                          {opportunitiesData[opportunityId].format && (
                                                            <div className="flex items-center gap-1">
                                                              <Building2 className="h-3.5 w-3.5" />
                                                              Format: {opportunitiesData[opportunityId].format.replace(/_/g, ' ')}
                                                            </div>
                                                          )}
                                                          {opportunitiesData[opportunityId].location && (
                                                            <div className="flex items-center gap-1">
                                                              <MapPin className="h-3.5 w-3.5" />
                                                              Location: {opportunitiesData[opportunityId].location}
                                                            </div>
                                                          )}
                                                          {opportunitiesData[opportunityId].focus_area && (
                                                            <div className="flex items-center gap-1">
                                                              <Target className="h-3.5 w-3.5" />
                                                              Focus: {opportunitiesData[opportunityId].focus_area.replace(/_/g, ' ')}
                                                            </div>
                                                          )}
                                                          {opportunitiesData[opportunityId].volunteer_support && (
                                                            <div className="flex items-center gap-1">
                                                              <Users className="h-3.5 w-3.5" />
                                                              Support: {opportunitiesData[opportunityId].volunteer_support}
                                                            </div>
                                                          )}
                                                        </div>
                                                        {opportunitiesData[opportunityId].community_need && (
                                                          <FormDescription>
                                                            {opportunitiesData[opportunityId].community_need}
                                                          </FormDescription>
                                                        )}
                                                      </div>
                                                    ) : (
                                                      <FormLabel className="font-medium cursor-pointer">
                                                        Opportunity ID: {opportunityId}
                                                      </FormLabel>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              <FormMessage />
                                            </FormItem>
                                          );
                                        }}
                                      />
                                    ))
                                ) : (
                                  <p className="text-muted-foreground">No opportunities specified for this campaign.</p>
                                )}
                                
                                {/* Show no results message when search has no matches */}
                                {rfq.event_campaign.meta.opportunities && 
                                 rfq.event_campaign.meta.opportunities.length > 0 && 
                                 searchQuery && 
                                 rfq.event_campaign.meta.opportunities.filter(opportunityId => {
                                   const opportunity = opportunitiesData[opportunityId];
                                   if (!opportunity) return false;
                                   
                                   const searchLower = searchQuery.toLowerCase();
                                   return (
                                     (opportunity.name && opportunity.name.toLowerCase().includes(searchLower)) ||
                                     (opportunity.format && opportunity.format.toLowerCase().includes(searchLower)) ||
                                     (opportunity.focus_area && opportunity.focus_area.toLowerCase().includes(searchLower)) ||
                                     (opportunity.community_need && opportunity.community_need.toLowerCase().includes(searchLower)) ||
                                     (opportunity.volunteer_support && opportunity.volunteer_support.toLowerCase().includes(searchLower))
                                   );
                                 }).length === 0 && (
                                   <div className="p-4 text-center border rounded-md">
                                     <p className="text-muted-foreground">No opportunities match your search. Try different keywords.</p>
                                   </div>
                                 )}
                              </div>
                            </ScrollArea>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Step 2: Opportunity-specific locations and pricing */}
                  {currentStep === 2 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-4">
                        <DollarSign className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-semibold">Rate Card Information</h2>
                      </div>
                      
                      {/* Price Recommendations Section */}
                      {loadingRecommendations ? (
                        <div className="mb-6 p-4 border rounded-md bg-muted/50">
                          <div className="flex items-center gap-2 mb-2">
                            <Info className="h-5 w-5 text-primary" />
                            <h3 className="text-md font-medium">Loading Price Recommendations...</h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-4 w-full" />
                          </div>
                        </div>
                      ) : Object.keys(priceRecommendations).length > 0 ? (
                        <div className="mb-6 p-4 border rounded-md bg-muted/50">
                          <div className="flex items-center gap-2 mb-2">
                            <Info className="h-5 w-5 text-primary" />
                            <h3 className="text-md font-medium">Price Recommendations</h3>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            Based on similar opportunities, we've pre-filled pricing information for you. Feel free to adjust as needed.
                          </p>
                          <div className="space-y-3">
                            {form.watch("supportedOpportunities").map(opportunityId => {
                              const recommendation = priceRecommendations[opportunityId];
                              if (!recommendation) return null;
                              
                              return (
                                <div key={`rec-${opportunityId}`} className="border-t pt-2">
                                  <h4 className="font-medium text-sm">
                                    {opportunitiesData[opportunityId]?.name || `Opportunity ${opportunityId}`}
                                  </h4>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs">
                                    {recommendation.base_cost > 0 && (
                                      <div className="flex justify-between">
                                        <span>Base Cost ({recommendation.pricing_model === "per_volunteer" ? "per volunteer" : "per event"}):</span>
                                        <span className="font-medium">{recommendation.currency || "USD"} {recommendation.base_cost.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {recommendation.program_management_fee > 0 && (
                                      <div className="flex justify-between">
                                        <span>Program Management ({getFeeTypeLabel(recommendation.program_management_fee_type)}):</span>
                                        <span className="font-medium">{recommendation.currency || "USD"} {recommendation.program_management_fee.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {recommendation.material_kit_fee > 0 && (
                                      <div className="flex justify-between">
                                        <span>Materials/Kit ({getFeeTypeLabel(recommendation.material_kit_fee_type)}):</span>
                                        <span className="font-medium">{recommendation.currency || "USD"} {recommendation.material_kit_fee.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {recommendation.refreshments_fee > 0 && (
                                      <div className="flex justify-between">
                                        <span>Refreshments ({getFeeTypeLabel(recommendation.refreshments_fee_type)}):</span>
                                        <span className="font-medium">{recommendation.currency || "USD"} {recommendation.refreshments_fee.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {recommendation.goodies_fee > 0 && (
                                      <div className="flex justify-between">
                                        <span>Goodies ({getFeeTypeLabel(recommendation.goodies_fee_type)}):</span>
                                        <span className="font-medium">{recommendation.currency || "USD"} {recommendation.goodies_fee.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {recommendation.supported_locations && recommendation.supported_locations.length > 0 && (
                                      <div className="col-span-2 mt-1">
                                        <span className="font-medium">Supported Locations:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {recommendation.supported_locations.map((location: string) => {
                                            const parts = location.split('-');
                                            const formattedLocation = parts.length === 3 
                                              ? `${parts[2]}, ${parts[1]}, ${parts[0]}` 
                                              : location;
                                            
                                            return (
                                              <span key={location} className="px-2 py-0.5 bg-muted rounded-full text-xs">
                                                {formattedLocation}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                    {recommendation.additional_details && (
                                      <div className="col-span-2 mt-1">
                                        <span className="font-medium">Additional Details:</span>
                                        <p className="text-xs mt-0.5">{recommendation.additional_details}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                      
                      {/* Currency and Max Volunteers section */}
                      <div className="space-y-4 border p-4 rounded-md">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="h-5 w-5 text-primary" />
                          <h2 className="text-lg font-semibold">Global Settings</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="currency"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Currency</FormLabel>
                                <FormControl>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select currency" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {currencies.map((currency) => (
                                        <SelectItem key={currency.value} value={currency.value}>
                                          {currency.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          

                        </div>
                      </div>
                      
                      {/* Opportunity rate cards */}
                      {form.watch("supportedOpportunities").length > 0 && (
                        <div className="space-y-4">
                          {form.watch("supportedOpportunities").map((opportunityId) => {
                            const opportunity = opportunitiesData[opportunityId];
                            // Map opportunity format to event type
                            let eventType = "Virtual"; // Default
                            if (opportunity?.format) {
                              if (opportunity.format === "In_Office") {
                                eventType = "InOffice";
                              } else if (opportunity.format === "Outdoor") {
                                eventType = "Outdoor";
                              } else if (opportunity.format === "Virtual") {
                                eventType = "Virtual";
                              }
                            }
                            
                            return (
                              <div key={opportunityId} className="space-y-4 border p-4 rounded-md relative overflow-hidden">
                                {/* Background image or gradient for the opportunity card */}
                                {opportunity?.image_url ? (
                                  <div 
                                    className="absolute inset-0 bg-cover bg-center opacity-5 z-0" 
                                    style={{ 
                                      backgroundImage: `url(${opportunity.image_url})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center'
                                    }}
                                  />
                                ) : (
                                  <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-background/10 opacity-50 z-0" />
                                )}
                                <div className="flex items-center gap-2 mb-2 relative z-10">
                                  <Briefcase className="h-5 w-5 text-primary" />
                                  <h2 className="text-lg font-semibold">
                                    {opportunity?.name || `Opportunity ID: ${opportunityId}`}
                                  </h2>
                                </div>
                                
                                {/* Locations for this opportunity - Only shown for non-virtual events */}
                                {eventType !== "Virtual" ? (
                                  <div className="space-y-4 border p-4 rounded-md relative z-10 bg-background/80">
                                    <div className="flex items-center gap-2 mb-2 relative z-10">
                                      <MapPin className="h-5 w-5 text-primary" />
                                      <h2 className="text-base font-semibold">Supported Locations</h2>
                                    </div>
                                    
                                    <FormField
                                      control={form.control}
                                      name={`opportunityRateCards.${opportunityId}.supportedLocations`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <div className="mb-4">
                                            <FormLabel>Select the locations where you can support this opportunity</FormLabel>
                                            <FormDescription>
                                              Choose the specific locations where your organization can provide this opportunity.
                                            </FormDescription>
                                          </div>
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {rfq?.event_campaign?.meta?.locations && rfq?.event_campaign?.meta?.locations?.length > 0 ? (
                                              rfq?.event_campaign?.meta?.locations?.map((location, index) => {
                                                const locationId = `${location.country}-${location.state}-${location.district}`;
                                                return (
                                                  <FormItem
                                                    key={locationId}
                                                    className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4"
                                                  >
                                                    <FormControl>
                                                      <Checkbox
                                                        checked={field.value?.includes(locationId)}
                                                        onCheckedChange={(checked) => {
                                                          return checked
                                                            ? field.onChange([...field.value, locationId])
                                                            : field.onChange(
                                                                field.value?.filter(
                                                                  (value) => value !== locationId
                                                                )
                                                              );
                                                        }}
                                                      />
                                                    </FormControl>
                                                    <div className="space-y-1 leading-none">
                                                      <FormLabel className="font-medium">
                                                        {location.district}, {location.state}, {location.country}
                                                      </FormLabel>
                                                      <FormDescription className="text-xs">
                                                        Volunteers needed: {location.meta?.volunteers}
                                                        {location.meta?.details && (
                                                          <div className="mt-1">Details: {location.meta?.details}</div>
                                                        )}
                                                      </FormDescription>
                                                    </div>
                                                  </FormItem>
                                                );
                                              })
                                            ) : (
                                              <p className="text-muted-foreground col-span-2">No locations specified for this campaign.</p>
                                            )}
                                          </div>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                ) : (
                                  // For Virtual events, we still need to set a default location value
                                  <FormField
                                    control={form.control}
                                    name={`opportunityRateCards.${opportunityId}.supportedLocations`}
                                    render={({ field }) => {
                                      // If this is a virtual event, automatically set "Virtual" as the location
                                      useEffect(() => {
                                        if (eventType === "Virtual" && (!field.value || field.value.length === 0)) {
                                          field.onChange(["Virtual"]);
                                        }
                                      }, [eventType, field]);
                                      
                                      return null; // Don't render anything for virtual events
                                    }}
                                  />
                                )}
                                
                                {/* Base pricing section */}
                                <div className="space-y-4 border p-4 rounded-md relative z-10 bg-background/80">
                                  <div className="flex items-center gap-2 mb-2 relative z-10">
                                    <DollarSign className="h-5 w-5 text-primary" />
                                    <h2 className="text-base font-semibold">
                                      Base Pricing ({eventType} Format)
                                    </h2>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                      control={form.control}
                                      name={`opportunityRateCards.${opportunityId}.basePricingVariant.pricingModel`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Pricing Model</FormLabel>
                                          <FormControl>
                                            <Select 
                                              onValueChange={field.onChange} 
                                              defaultValue={field.value}
                                              disabled={eventType === "Virtual"}
                                            >
                                              <FormControl>
                                                <SelectTrigger>
                                                  <SelectValue placeholder="Select pricing model" />
                                                </SelectTrigger>
                                              </FormControl>
                                              <SelectContent>
                                                <SelectItem value="per_volunteer">Cost per Volunteer</SelectItem>
                                                <SelectItem value="per_event">Cost per Event</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    
                                    <FormField
                                      control={form.control}
                                      name={`opportunityRateCards.${opportunityId}.basePricingVariant.costPerUnit`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>
                                            {form.watch(`opportunityRateCards.${opportunityId}.basePricingVariant.pricingModel`) === "per_volunteer" 
                                              ? "Cost per Volunteer" 
                                              : "Cost per Event"}
                                          </FormLabel>
                                          <FormControl>
                                            <div className="relative">
                                              {form.watch("currency") === "USD" && <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />}
                                              {form.watch("currency") === "EUR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">€</span>}
                                              {form.watch("currency") === "GBP" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">£</span>}
                                              {form.watch("currency") === "JPY" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">¥</span>}
                                              {form.watch("currency") === "CAD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">C$</span>}
                                              {form.watch("currency") === "AUD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">A$</span>}
                                              {form.watch("currency") === "SGD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">S$</span>}
                                              {form.watch("currency") === "INR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">₹</span>}
                                              {!["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "SGD", "INR"].includes(form.watch("currency")) && 
                                                <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">{form.watch("currency")}</span>}
                                              <Input 
                                                {...field} 
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="pl-8"
                                                onWheel={(e) => e.currentTarget.blur()}
                                              />
                                            </div>
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                      control={form.control}
                                      name={`opportunityRateCards.${opportunityId}.basePricingVariant.minVolunteers`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Minimum Volunteers</FormLabel>
                                          <FormControl>
                                            <Input 
                                              {...field} 
                                              type="number"
                                              min="1"
                                              onWheel={(e) => e.currentTarget.blur()}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    
                                    <FormField
                                      control={form.control}
                                      name={`opportunityRateCards.${opportunityId}.maxVolunteers`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Maximum Volunteers</FormLabel>
                                          <FormControl>
                                            <Input 
                                              {...field} 
                                              type="number"
                                              min="1"
                                              onWheel={(e) => e.currentTarget.blur()}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                </div>
                                
                                {/* Additional fees section */}
                                <div className="space-y-4 border p-4 rounded-md relative z-10 bg-background/80">
                                  <div className="flex items-center gap-2 mb-2 relative z-10">
                                    <CheckCircle2 className="h-5 w-5 text-primary" />
                                    <h2 className="text-base font-semibold">Additional Fees</h2>
                                  </div>
                                  
                                  <FormField
                                    control={form.control}
                                    name={`opportunityRateCards.${opportunityId}.includeFees`}
                                    render={({ field }) => (
                                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                        <FormControl>
                                          <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                          />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                          <FormLabel>Include fees in base price</FormLabel>
                                          <FormDescription>
                                            If checked, all fees below will be included in the base price. If unchecked, they will be itemized separately.
                                          </FormDescription>
                                        </div>
                                      </FormItem>
                                    )}
                                  />
                                  
                                  {!form.watch(`opportunityRateCards.${opportunityId}.includeFees`) && (
                                    <div className="space-y-4">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.programManagementFee`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Program Management Fee</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  {form.watch("currency") === "USD" && <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />}
                                                  {form.watch("currency") === "EUR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">€</span>}
                                                  {form.watch("currency") === "GBP" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">£</span>}
                                                  {form.watch("currency") === "JPY" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">¥</span>}
                                                  {form.watch("currency") === "CAD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">C$</span>}
                                                  {form.watch("currency") === "AUD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">A$</span>}
                                                  {form.watch("currency") === "SGD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">S$</span>}
                                                  {form.watch("currency") === "INR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">₹</span>}
                                                  {!["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "SGD", "INR"].includes(form.watch("currency")) && 
                                                    <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">{form.watch("currency")}</span>}
                                                  <Input 
                                                    {...field} 
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="pl-8"
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                        
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.programManagementFeeType`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Program Management Fee Type</FormLabel>
                                              <FormControl>
                                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                  <FormControl>
                                                    <SelectTrigger>
                                                      <SelectValue placeholder="Select fee type" />
                                                    </SelectTrigger>
                                                  </FormControl>
                                                  <SelectContent>
                                                    <SelectItem value="total">Total Program Cost</SelectItem>
                                                    <SelectItem value="per_event">Per Event</SelectItem>
                                                    <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      </div>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.materialKitFee`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Material/Kit Fee</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  {form.watch("currency") === "USD" && <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />}
                                                  {form.watch("currency") === "EUR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">€</span>}
                                                  {form.watch("currency") === "GBP" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">£</span>}
                                                  {form.watch("currency") === "JPY" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">¥</span>}
                                                  {form.watch("currency") === "CAD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">C$</span>}
                                                  {form.watch("currency") === "AUD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">A$</span>}
                                                  {form.watch("currency") === "SGD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">S$</span>}
                                                  {form.watch("currency") === "INR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">₹</span>}
                                                  {!["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "SGD", "INR"].includes(form.watch("currency")) && 
                                                    <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">{form.watch("currency")}</span>}
                                                  <Input 
                                                    {...field} 
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="pl-8"
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                        
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.materialKitFeeType`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Material Kit Fee Type</FormLabel>
                                              <FormControl>
                                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                  <FormControl>
                                                    <SelectTrigger>
                                                      <SelectValue placeholder="Select fee type" />
                                                    </SelectTrigger>
                                                  </FormControl>
                                                  <SelectContent>
                                                    <SelectItem value="total">Total Program Cost</SelectItem>
                                                    <SelectItem value="per_event">Per Event</SelectItem>
                                                    <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      </div>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.refreshmentsFee`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Refreshments Fee</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  {form.watch("currency") === "USD" && <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />}
                                                  {form.watch("currency") === "EUR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">€</span>}
                                                  {form.watch("currency") === "GBP" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">£</span>}
                                                  {form.watch("currency") === "JPY" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">¥</span>}
                                                  {form.watch("currency") === "CAD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">C$</span>}
                                                  {form.watch("currency") === "AUD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">A$</span>}
                                                  {form.watch("currency") === "SGD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">S$</span>}
                                                  {form.watch("currency") === "INR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">₹</span>}
                                                  {!["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "SGD", "INR"].includes(form.watch("currency")) && 
                                                    <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">{form.watch("currency")}</span>}
                                                  <Input 
                                                    {...field} 
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="pl-8"
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                        
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.refreshmentsFeeType`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Refreshments Fee Type</FormLabel>
                                              <FormControl>
                                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                  <FormControl>
                                                    <SelectTrigger>
                                                      <SelectValue placeholder="Select fee type" />
                                                    </SelectTrigger>
                                                  </FormControl>
                                                  <SelectContent>
                                                    <SelectItem value="total">Total Program Cost</SelectItem>
                                                    <SelectItem value="per_event">Per Event</SelectItem>
                                                    <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      </div>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.goodiesFee`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Goodies Fee</FormLabel>
                                              <FormControl>
                                                <div className="relative">
                                                  {form.watch("currency") === "USD" && <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />}
                                                  {form.watch("currency") === "EUR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">€</span>}
                                                  {form.watch("currency") === "GBP" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">£</span>}
                                                  {form.watch("currency") === "JPY" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">¥</span>}
                                                  {form.watch("currency") === "CAD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">C$</span>}
                                                  {form.watch("currency") === "AUD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">A$</span>}
                                                  {form.watch("currency") === "SGD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">S$</span>}
                                                  {form.watch("currency") === "INR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">₹</span>}
                                                  {!["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "SGD", "INR"].includes(form.watch("currency")) && 
                                                    <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">{form.watch("currency")}</span>}
                                                  <Input 
                                                    {...field} 
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="pl-8"
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                  />
                                                </div>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                        
                                        <FormField
                                          control={form.control}
                                          name={`opportunityRateCards.${opportunityId}.goodiesFeeType`}
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Goodies Fee Type</FormLabel>
                                              <FormControl>
                                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                  <FormControl>
                                                    <SelectTrigger>
                                                      <SelectValue placeholder="Select fee type" />
                                                    </SelectTrigger>
                                                  </FormControl>
                                                  <SelectContent>
                                                    <SelectItem value="total">Total Program Cost</SelectItem>
                                                    <SelectItem value="per_event">Per Event</SelectItem>
                                                    <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Cost breakdown preview */}
                                {form.watch(`opportunityRateCards.${opportunityId}.basePricingVariant.costPerUnit`) && (
                                  <div className="bg-muted/50 p-4 rounded-md border mt-4">
                                    <div className="flex items-center gap-2 mb-2 relative z-10">
                                      <CheckCircle2 className="h-4 w-4 text-primary" />
                                      <p className="text-sm font-medium">Cost Breakdown (USD Equivalent)</p>
                                    </div>
                                    
                                    {/* Calculate costs */}
                                    {(() => {
                                      const rateCard = form.watch(`opportunityRateCards.${opportunityId}`);
                                      if (!rateCard || !rateCard.basePricingVariant.costPerUnit) return null;
                                      
                                      const baseCost = Number(rateCard.basePricingVariant.costPerUnit) * exchangeRates[form.watch("currency")];
                                      
                                      const programFee = rateCard.programManagementFee ? 
                                        parseFloat(rateCard.programManagementFee) * exchangeRates[form.watch("currency")] : 0;
                                      const materialFee = rateCard.materialKitFee ? 
                                        parseFloat(rateCard.materialKitFee) * exchangeRates[form.watch("currency")] : 0;
                                      const refreshmentsFee = rateCard.refreshmentsFee ? 
                                        parseFloat(rateCard.refreshmentsFee) * exchangeRates[form.watch("currency")] : 0;
                                      const goodiesFee = rateCard.goodiesFee ? 
                                        parseFloat(rateCard.goodiesFee) * exchangeRates[form.watch("currency")] : 0;
                                      
                                      const programFeeType = rateCard.programManagementFeeType;
                                      const materialFeeType = rateCard.materialKitFeeType;
                                      const refreshmentsFeeType = rateCard.refreshmentsFeeType;
                                      const goodiesFeeType = rateCard.goodiesFeeType;
                                      
                                      const totalAdditionalFees = programFee + materialFee + refreshmentsFee + goodiesFee;
                                      const totalCost = baseCost + (rateCard.includeFees ? 0 : totalAdditionalFees);
                                      

                                      
                                      return (
                                        <div className="space-y-2 text-sm text-muted-foreground">
                                          <div className="pl-2 border-l-2 border-primary/20">
                                            <div className="flex justify-between text-xs">
                                              <span>Base Cost ({eventType}):</span>
                                              <span className="font-medium">
                                                ${baseCost.toFixed(2)} USD
                                                {rateCard.basePricingVariant.pricingModel === "per_volunteer" 
                                                  ? " per volunteer" 
                                                  : rateCard.basePricingVariant.pricingModel === "per_event" 
                                                    ? " per event" 
                                                    : ""}
                                              </span>
                                            </div>
                                            
                                            {rateCard.includeFees ? (
                                              <div className="text-xs italic">
                                                All additional fees are included in the base cost
                                              </div>
                                            ) : (
                                              <>
                                                {programFee > 0 && (
                                                  <div className="flex justify-between">
                                                    <span>Program Management:</span>
                                                    <span>${programFee.toFixed(2)} USD ({getFeeTypeLabel(programFeeType)})</span>
                                                  </div>
                                                )}
                                                
                                                {materialFee > 0 && (
                                                  <div className="flex justify-between">
                                                    <span>Materials/Kits:</span>
                                                    <span>${materialFee.toFixed(2)} USD ({getFeeTypeLabel(materialFeeType)})</span>
                                                  </div>
                                                )}
                                                
                                                {refreshmentsFee > 0 && (
                                                  <div className="flex justify-between">
                                                    <span>Refreshments:</span>
                                                    <span>${refreshmentsFee.toFixed(2)} USD ({getFeeTypeLabel(refreshmentsFeeType)})</span>
                                                  </div>
                                                )}
                                                
                                                {goodiesFee > 0 && (
                                                  <div className="flex justify-between">
                                                    <span>Goodies:</span>
                                                    <span>${goodiesFee.toFixed(2)} USD ({getFeeTypeLabel(goodiesFeeType)})</span>
                                                  </div>
                                                )}
                                                
                                                {totalAdditionalFees > 0 && (
                                                  <div className="flex justify-between pt-1 border-t">
                                                    <span className="font-medium">Total Cost:</span>
                                                    <span className="font-medium">${totalCost.toFixed(2)} USD</span>
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                                
                                {/* Geographic pricing adjustments */}
                                {form.watch(`opportunityRateCards.${opportunityId}.enableGeographicPricing`) && (
                                  <div className="space-y-4 border p-4 rounded-md relative z-10 bg-background/80">
                                    <div className="flex items-center gap-2 mb-2 relative z-10">
                                      <MapPin className="h-5 w-5 text-primary" />
                                      <h2 className="text-base font-semibold">Geographic Pricing Adjustments</h2>
                                    </div>
                                    
                                    <FormField
                                      control={form.control}
                                      name={`opportunityRateCards.${opportunityId}.enableGeographicPricing`}
                                      render={({ field }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value}
                                              onCheckedChange={field.onChange}
                                            />
                                          </FormControl>
                                          <div className="space-y-1 leading-none">
                                            <FormLabel>Enable Geographic Pricing Adjustments</FormLabel>
                                            <FormDescription>
                                              Set different pricing for different locations based on regional costs.
                                            </FormDescription>
                                          </div>
                                        </FormItem>
                                      )}
                                    />
                                    
                                    {form.watch(`opportunityRateCards.${opportunityId}.enableGeographicPricing`) && (
                                      <FormField
                                        control={form.control}
                                        name={`opportunityRateCards.${opportunityId}.geographicMarkups`}
                                        render={({ field }) => {
                                          // Use useFieldArray hook to manage the array of markups
                                          const { fields, append, remove } = useFieldArray({
                                            control: form.control,
                                            name: `opportunityRateCards.${opportunityId}.geographicMarkups`
                                          });
                                          
                                          return (
                                            <FormItem>
                                              <div className="space-y-4">
                                                {fields.map((item, index) => (
                                                  <div key={item.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 border rounded-md">
                                                    <FormField
                                                      control={form.control}
                                                      name={`opportunityRateCards.${opportunityId}.geographicMarkups.${index}.locationId`}
                                                      render={({ field }) => (
                                                        <FormItem>
                                                          <FormLabel>Location</FormLabel>
                                                          <FormControl>
                                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                              <FormControl>
                                                                <SelectTrigger>
                                                                  <SelectValue placeholder="Select location" />
                                                                </SelectTrigger>
                                                              </FormControl>
                                                              <SelectContent>
                                                                {rfq?.event_campaign?.meta?.locations && rfq.event_campaign.meta.locations.length > 0 ? (
                                                                  rfq.event_campaign.meta.locations.map((location) => {
                                                                    const locationId = `${location.country}-${location.state}-${location.district}`;
                                                                    const locationParts = locationId.split('-');
                                                                    const locationName = locationParts.length === 3 
                                                                      ? `${locationParts[2]}, ${locationParts[1]}, ${locationParts[0]}`
                                                                      : locationId;
                                                                    
                                                                    return (
                                                                      <SelectItem key={locationId} value={locationId}>
                                                                        {locationName}
                                                                      </SelectItem>
                                                                    );
                                                                  })
                                                                ) : (
                                                                  <SelectItem value="">Please select locations in Step 1</SelectItem>
                                                                )}
                                                              </SelectContent>
                                                            </Select>
                                                          </FormControl>
                                                          <FormMessage />
                                                        </FormItem>
                                                      )}
                                                    />
                                                    
                                                    <FormField
                                                      control={form.control}
                                                      name={`opportunityRateCards.${opportunityId}.geographicMarkups.${index}.markupPercentage`}
                                                      render={({ field }) => (
                                                        <FormItem>
                                                          <FormLabel>Markup Percentage</FormLabel>
                                                          <FormControl>
                                                            <div className="relative">
                                                              <Percent className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                                              <Input 
                                                                {...field} 
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                className="pl-8"
                                                                onWheel={(e) => e.currentTarget.blur()}
                                                              />
                                                            </div>
                                                          </FormControl>
                                                          <FormMessage />
                                                        </FormItem>
                                                      )}
                                                    />
                                                    
                                                    <div className="flex items-end justify-center md:justify-start">
                                                      <Button 
                                                        type="button"
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={() => remove(index)}
                                                        className="mb-2"
                                                      >
                                                        <X className="h-4 w-4" />
                                                      </Button>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                              <Button 
                                                type="button" 
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  append({ locationId: "", markupPercentage: "" });
                                                }}
                                                className="mt-4"
                                              >
                                                Add Location
                                              </Button>
                                              <FormMessage />
                                            </FormItem>
                                          );
                                        }}
                                      />
                                    )}
                                    
                                    {/* Geographic pricing preview */}
                                    {form.watch(`opportunityRateCards.${opportunityId}.enableGeographicPricing`) && 
                                     form.watch(`opportunityRateCards.${opportunityId}.geographicMarkups`)?.length > 0 && 
                                     form.watch(`opportunityRateCards.${opportunityId}.basePricingVariant.costPerUnit`) && (
                                      <div className="bg-muted/50 p-4 rounded-md border mt-4">
                                        <div className="flex items-center gap-2 mb-2 relative z-10">
                                          <MapPin className="h-4 w-4 text-primary" />
                                          <p className="text-sm font-medium">Geographic Pricing Preview</p>
                                        </div>
                                        
                                        {(() => {
                                          const rateCard = form.watch(`opportunityRateCards.${opportunityId}`);
                                          if (!rateCard || !rateCard.basePricingVariant.costPerUnit) return null;
                                          
                                          const baseCost = Number(rateCard.basePricingVariant.costPerUnit) * exchangeRates[form.watch("currency")];
                                          
                                          const programFee = rateCard.programManagementFee ? 
                                            parseFloat(rateCard.programManagementFee) * exchangeRates[form.watch("currency")] : 0;
                                          const materialFee = rateCard.materialKitFee ? 
                                            parseFloat(rateCard.materialKitFee) * exchangeRates[form.watch("currency")] : 0;
                                          const refreshmentsFee = rateCard.refreshmentsFee ? 
                                            parseFloat(rateCard.refreshmentsFee) * exchangeRates[form.watch("currency")] : 0;
                                          const goodiesFee = rateCard.goodiesFee ? 
                                            parseFloat(rateCard.goodiesFee) * exchangeRates[form.watch("currency")] : 0;
                                          
                                          const totalAdditionalFees = programFee + materialFee + refreshmentsFee + goodiesFee;
                                          const totalCost = baseCost + (rateCard.includeFees ? 0 : totalAdditionalFees);
                                          
                                          return (
                                            <div className="space-y-2 text-sm text-muted-foreground">
                                              {rateCard.geographicMarkups.map((markup, index) => {
                                                if (!markup.locationId || !markup.markupPercentage) return null;
                                                
                                                const markupPercent = parseFloat(markup.markupPercentage);
                                                const markupAmount = totalCost * (markupPercent / 100);
                                                const adjustedTotal = totalCost + markupAmount;
                                                
                                                const locationParts = markup.locationId.split('-');
                                                const locationName = locationParts.length === 3 
                                                  ? `${locationParts[2]}, ${locationParts[1]}, ${locationParts[0]}`
                                                  : markup.locationId;
                                                
                                                return (
                                                  <div key={index} className="pl-2 border-l-2 border-primary/20 mb-2">
                                                    <div className="font-medium">{locationName}</div>
                                                    <div className="flex justify-between text-xs">
                                                      <span>Base Cost:</span>
                                                      <span>${totalCost.toFixed(2)} USD</span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                      <span>Markup ({markupPercent}%):</span>
                                                      <span>+${markupAmount.toFixed(2)} USD</span>
                                                    </div>
                                                    <div className="flex justify-between pt-1 border-t">
                                                      <span className="font-medium">Adjusted Total:</span>
                                                      <span className="font-medium">${adjustedTotal.toFixed(2)} USD</span>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Additional details section */}
                      <div className="space-y-4 border p-4 rounded-md">
                        <div className="flex items-center gap-2 mb-2">
                          <Info className="h-5 w-5 text-primary" />
                          <h2 className="text-lg font-semibold">Additional Information</h2>
                        </div>
                        
                        <FormField
                          control={form.control}
                          name="additionalDetails"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Additional Details (Optional)</FormLabel>
                              <FormDescription>
                                Provide any additional information about your pricing, services, or requirements.
                              </FormDescription>
                              <FormControl>
                                <Textarea 
                                  placeholder="Enter any additional details here..." 
                                  className="min-h-[100px]" 
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  )}
                  
                </form>
              </Form>

            </CardContent>
            <CardFooter className="flex justify-between">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  Previous
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/dashboard/corporate-events/rfqs')}>
                  Cancel
                </Button>
              )}
              
              {currentStep < 2 ? (
                <Button 
                  onClick={nextStep} 
                  disabled={loadingRecommendations}
                >
                  {loadingRecommendations ? (
                    <>
                      <span className="mr-2 h-4 w-4 animate-spin">◌</span>
                      Loading Recommendations...
                    </>
                  ) : "Next"}
                </Button>
              ) : (
                <Button onClick={form.handleSubmit(onSubmit)} disabled={submitMutation.isPending}>
                  {submitMutation.isPending ? "Submitting..." : "Submit Response"}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}